import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { IMedicalQuestion, IMedicalQuestionData } from "src/app-telehealth-demo/util/utils";
import { isEmptyObject } from "src/utils";
import * as yup from "yup";

interface MedicalQuestionsStepProps {
    questions: IMedicalQuestion[];
    prefillFields: IMedicalQuestionData;
    updatePrefillFields: (data: Partial<IMedicalQuestionData>) => void;
    onContinue: () => void;
    onBack: () => void;
}

const MedicalQuestionsStep: React.FC<MedicalQuestionsStepProps> = ({
    questions,
    prefillFields,
    updatePrefillFields,
    onContinue,
    onBack,
}) => {
    const buildValidationSchema = () => {
        const schemaShape: { [key: string]: any } = {};

        questions.forEach((question) => {
            let fieldSchema = yup.string();

            if (question.required) {
                fieldSchema = fieldSchema
                    .required(`${question.name} is required`)
                    .min(1, `${question.name} is required`);
            }

            // Add conditional validation if specified
            if (question.dependsOn) {
                fieldSchema = fieldSchema.when(question.dependsOn.field, {
                    is: question.dependsOn.value.toString(),
                    then: (schema) => schema.required(`${question.name} is required`),
                    otherwise: (schema) => schema.notRequired(),
                });
            }

            schemaShape[question.field] = fieldSchema;
        });

        return yup.object().shape(schemaShape);
    };

    const schema = buildValidationSchema();

    // Initialize react-hook-form
    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        mode: "onBlur",
        defaultValues: prefillFields || {},
    });

    // Watch all fields for changes
    const watchedValues = watch();

    // Update parent component when form values change
    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (type === "change") {
                updatePrefillFields(value);
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, updatePrefillFields, questions, setValue]);

    const onSubmit = (data: IMedicalQuestionData) => {
        // Filter out values for fields that depend on conditions not met
        const filteredData = { ...data };
        questions.forEach((question) => {
            if (question.dependsOn) {
                const dependencyMet =
                    filteredData[question.dependsOn.field] === question.dependsOn.value.toString();

                const field = question.field;
                if (
                    field in filteredData &&
                    (!dependencyMet || isEmptyObject(filteredData[field]))
                ) {
                    delete filteredData[field];
                }
            }
        });

        updatePrefillFields(filteredData);
        onContinue();
    };

    // Helper to render the appropriate form control based on question type
    const renderField = (question: IMedicalQuestion) => {
        const { name, field, type, options, dependsOn } = question;

        // Check if field should be shown based on dependency
        if (dependsOn && watchedValues[dependsOn.field] !== dependsOn.value.toString()) {
            return null;
        }

        switch (type) {
            case "checkbox":
                return (
                    <Form.Group className="mb-3" key={field}>
                        <Form.Check
                            type="checkbox"
                            id={field}
                            label={name}
                            isInvalid={!!errors[field]}
                            {...register(field)}
                            onChange={(e) => {
                                const event = {
                                    target: {
                                        name: field,
                                        value: e.target.checked ? "true" : "false",
                                    },
                                } as React.ChangeEvent<HTMLInputElement>;
                                register(field).onChange(event);
                            }}
                            checked={watchedValues[field] === "true"}
                        />
                        {errors[field] && (
                            <Form.Control.Feedback type="invalid">
                                {errors[field]?.message as string}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                );

            case "radio":
                return (
                    <Form.Group className="mb-3" key={field}>
                        <Form.Label>{name}</Form.Label>
                        <div>
                            {options?.map((option) => (
                                <Form.Check
                                    key={option}
                                    type="radio"
                                    id={`${field}-${option}`}
                                    label={option}
                                    value={option}
                                    isInvalid={!!errors[field]}
                                    {...register(field)}
                                    inline
                                />
                            ))}
                        </div>
                        {errors[field] && (
                            <Form.Control.Feedback type="invalid">
                                {errors[field]?.message as string}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                );

            case "select":
                return (
                    <Form.Group className="mb-3" key={field}>
                        <Form.Label>{name}</Form.Label>
                        <Form.Select isInvalid={!!errors[field]} {...register(field)}>
                            <option value="">Select an option</option>
                            {options?.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </Form.Select>
                        {errors[field] && (
                            <Form.Control.Feedback type="invalid">
                                {errors[field]?.message as string}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                );

            case "text":
                return (
                    <Form.Group className="mb-3" key={field}>
                        <Form.Label>{name}</Form.Label>
                        <Form.Control
                            type="text"
                            isInvalid={!!errors[field]}
                            {...register(field)}
                        />
                        {errors[field] && (
                            <Form.Control.Feedback type="invalid">
                                {errors[field]?.message as string}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                );
        }
    };

    // Group questions into categories if they share a common prefix
    const groupQuestions = () => {
        const groups: { [key: string]: IMedicalQuestion[] } = {};

        questions.forEach((question) => {
            const groupKey = question.field.split("_")[0] || "other";
            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }
            groups[groupKey].push(question);
        });

        return groups;
    };

    const questionGroups = groupQuestions();

    return (
        <div className="medical-questions-step">
            <h2 className="mb-4">Medical History & Lifestyle</h2>
            <p className="text-muted mb-4">
                Please provide the following information to help us provide the best care for you.
            </p>

            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                {Object.entries(questionGroups).map(([groupKey, groupQuestions]) => (
                    <Card className="mb-4" key={groupKey}>
                        <Card.Header>
                            <h5 className="mb-0">
                                {groupKey === "mh"
                                    ? "Medical History"
                                    : groupKey === "cma"
                                      ? "Current Medications & Allergies"
                                      : groupKey === "ld"
                                        ? "Lifestyle"
                                        : "Additional Information"}
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                {groupQuestions.map((question) => (
                                    <Col
                                        md={
                                            question.type === "checkbox" ||
                                            question.type === "radio"
                                                ? 6
                                                : 12
                                        }
                                        key={question.field}
                                    >
                                        {renderField(question)}
                                    </Col>
                                ))}
                            </Row>
                        </Card.Body>
                    </Card>
                ))}

                <div className="d-flex justify-content-between mt-4">
                    <Button variant="outline-secondary" onClick={onBack}>
                        Back
                    </Button>
                    <Button variant="primary" type="submit">
                        Continue
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default MedicalQuestionsStep;
